import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBag, X } from 'lucide-react';
import { previewPurchases, recentPurchases } from '../data/recentPurchases';
const INITIAL_DELAY_MS = 5000;
const VISIBLE_MS = 5000;
const GAP_MS = 4000;
export function PurchaseNotification() {
  const purchases = useMemo(() => recentPurchases.length > 0 ? recentPurchases : isEditorPreview() ? previewPurchases : [], []);
  const [index, setIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (isDismissed || purchases.length === 0) return;
    const delay = isVisible ? VISIBLE_MS : hasStarted ? GAP_MS : INITIAL_DELAY_MS;
    const timer = window.setTimeout(() => {
      if (isVisible) {
        setIsVisible(false);
        setIndex(current => (current + 1) % purchases.length);
      } else {
        setHasStarted(true);
        setIsVisible(true);
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [isVisible, isDismissed, hasStarted, purchases.length]);
  if (isDismissed || purchases.length === 0) return null;
  const purchase = purchases[index];
  const minutesLabel = `${purchase.minutesAgo} minute${purchase.minutesAgo === 1 ? '' : 's'} ago`;
  return <div className="fixed bottom-4 left-4 z-40 w-[320px] max-w-[calc(100vw-2rem)] pointer-events-none" aria-live="polite" data-id="element-214" data-mp="19nbsroo2p7eh">
      <AnimatePresence data-id="element-215" data-mp="zrqwm75or1cn">
        {isVisible && <motion.div key={`${purchase.firstName}-${purchase.city}-${index}`} initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 16,
        scale: 0.96
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.28,
        ease: [0.23, 1, 0.32, 1]
      }} className="pointer-events-auto relative flex items-center gap-3.5 bg-white rounded-2xl border border-[#081632]/[0.06] shadow-[0_12px_32px_-8px_rgba(8,22,50,0.22)] py-3 pl-3 pr-9 overflow-hidden" role="status" data-id="element-216" data-mp="zt1yg1fklz14">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full bg-[#D89B16]" aria-hidden="true" data-id="element-217" data-mp="2jnegkqjbgixv" />

            <div className="relative shrink-0 w-11 h-11 rounded-xl bg-[#081632] text-[#D89B16] flex items-center justify-center" data-id="element-218" data-mp="3ge6xtrhzkh18">
              <ShoppingBag size={19} strokeWidth={2.2} data-id="element-219" data-mp="3bf3v4dphamwf" />
            </div>

            <div className="min-w-0 flex-1" data-id="element-220" data-mp="37c7ir4qj7n0x">
              <p className="flex items-center gap-1.5 text-sm text-[#081632] leading-5 whitespace-nowrap" data-id="element-221" data-mp="37f3fwquk27ob">
                <img src={`https://flagcdn.com/${purchase.countryCode}.svg`} alt={`${purchase.country} flag`} className="w-[18px] h-[13px] rounded-[2px] object-cover shrink-0 ring-1 ring-black/5" data-id="element-222" data-mp="32g8ncx6sd4r0" />
                <span className="font-bold" data-id="element-223" data-mp="6i9i0ffv2jpk">{purchase.firstName}</span>
                <span className="text-[#081632]/70 truncate" data-id="element-224" data-mp="bezz963ejlck">
                  from {purchase.city}, {purchase.country}
                </span>
              </p>
              <p className="text-sm text-[#081632]/70 leading-5 whitespace-nowrap" data-id="element-225" data-mp="ffjta0soi8uh">
                purchased <span className="font-semibold text-[#081632]" data-id="element-226" data-mp="k9xuiju0va72">Judo Coach Ready</span>
              </p>
              <p className="flex items-center gap-1.5 text-xs text-slate-500 leading-4 mt-1" data-id="element-227" data-mp="p9k4mkek0abn">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D89B16]" aria-hidden="true" data-id="element-228" data-mp="u83totog2288" />
                {minutesLabel}
              </p>
            </div>

            <button type="button" onClick={() => setIsDismissed(true)} className="absolute top-2 right-2 w-6 h-6 rounded-full text-slate-400 hover:text-[#081632] hover:bg-[#F5F7FB] flex items-center justify-center transition-colors duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E4ED8]" aria-label="Close notification" data-id="element-229" data-mp="135ny2bhlsen2">
              <X size={13} data-id="element-230" data-mp="25on1rtzx1d2c" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function isEditorPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}